import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as contactsApi from '@/api/contacts.api';
import * as channelsApi from '@/api/channels.api';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { CanalDelClienteSelect } from '@/components/contacts/CanalDelClienteSelect';
import { useToast } from '@/hooks/useToast';
import { validateManualClient } from '@/lib/manual-client';
import { canalesWhatsApp, validarCanalDelCliente } from '@/lib/avisos-canal';

/**
 * Alta MANUAL de cliente (Fase 1 trabajo manual): nombre + teléfono + canal de
 * su vendedora, sin esperar a que escriba por WhatsApp. Si el teléfono ya
 * existía, el server devuelve el contacto existente (no duplica) y se avisa.
 */
export function NuevoClienteModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [nombre, setNombre] = useState('');
  const [telefono, setTelefono] = useState('');
  const [channelId, setChannelId] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Canal (vendedora) del cliente: de ese número le llegan sus avisos.
  const { data: channels } = useQuery({
    queryKey: ['channels', 'list'],
    queryFn: () => channelsApi.listChannels(),
    enabled: open,
  });
  const canales = useMemo(() => canalesWhatsApp(channels ?? []), [channels]);

  function close() {
    setNombre('');
    setTelefono('');
    setChannelId('');
    setError(null);
    onClose();
  }

  const create = useMutation({
    mutationFn: () =>
      contactsApi.createManualContact({ nombre, telefono, channelId: channelId || null }),
    onSuccess: ({ created }) => {
      void qc.invalidateQueries({ queryKey: ['contacts'] });
      if (created) toast.success('Cliente creado');
      else toast.info('Ese teléfono ya existía: es el cliente de la lista');
      close();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'No se pudo crear el cliente'),
  });

  function submit() {
    const v = validateManualClient({ nombre, telefono });
    if (!v.ok) {
      setError(v.error);
      return;
    }
    const canal = validarCanalDelCliente(canales, channelId);
    if (!canal.ok) {
      setError(canal.error);
      return;
    }
    setError(null);
    create.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title="Nuevo cliente"
      description="Registra a mano a alguien que compró por fuera (sin conversación de WhatsApp)."
      footer={
        <>
          <Button variant="ghost" type="button" onClick={close}>
            Cancelar
          </Button>
          <Button onClick={submit} loading={create.isPending}>
            Crear cliente
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label="Nombre"
          placeholder="Nombre del cliente"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
        />
        <Input
          label="Teléfono"
          placeholder="18095550000"
          value={telefono}
          onChange={(e) => setTelefono(e.target.value)}
        />
        <CanalDelClienteSelect canales={canales} value={channelId} onChange={setChannelId} />
        {error && <p className="text-sm text-danger">{error}</p>}
      </div>
    </Modal>
  );
}
